import {
  getDefaultNavigationTabs,
  hasDefaultIcons,
  preUploadDefaultIcons,
  selectNavigationTabsMutationData,
  useGetNavigationTabsQuery,
  useUpdateNavigationTabsMutation,
} from 'api/navigationTabs'
import useDispatch from 'hooks/useDispatch'
import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { selectApp } from 'selectors/app'
import { selectThemeId } from 'selectors/landingPageTheme'
import {
  selectCurrentMerchant,
  selectOrderingEnabled,
} from 'selectors/merchant'
import {
  reset,
  setChanges,
  setSubmitting,
} from 'slices/Cms/Content/AppNavigation/BottomNav'
import { reset as resetDraft } from 'slices/Cms/Content/AppNavigation/BottomNav/Draft'
import {
  selectBottomNavHasChanges,
  selectNavigationTabs,
} from 'slices/Cms/Content/AppNavigation/BottomNav/selectors'
import {
  convertDefaultTabsApiToSliceFormat,
  convertNavigationTabsApiToSliceFormat,
  convertNavigationTabsToApiFormat,
} from './helpers'

export const useFetchNavigationTabs = () => {
  const app = useSelector(selectApp)
  const themeId = useSelector(selectThemeId)
  return useGetNavigationTabsQuery(
    { appId: app?.id || 0, themeId: themeId || 0 },
    { skip: !app?.id || !themeId }
  )
}

// Automatically loads navigation_tabs data into Redux, based on the back-end API data
//   * If user has changes -> sync does nothing, preserves user's draft changes
//   * Otherwise load data from either `navigation_tabs` or `default_tabs`
//     whichever is available, in this order
//   * Uses the API query result directly to avoid timing issues
export const useSyncApiState = () => {
  const dispatch = useDispatch()
  const hasChanges = useSelector(selectBottomNavHasChanges)
  const app = useSelector(selectApp)
  const themeId = useSelector(selectThemeId)
  const orderingEnabled = useSelector(selectOrderingEnabled)
  // Sync with the latest response from the mutation, when updating tabs
  // to avoid unnecessary refetches
  const mutationResult = useSelector(selectNavigationTabsMutationData)

  // Use the query hook directly to get the actual API response
  const {
    data: apiResponse,
    isSuccess,
    isUninitialized,
    isLoading,
    fulfilledTimeStamp: queryFulfilledTimeStamp,
  } = useGetNavigationTabsQuery(
    { appId: app?.id || 0, themeId: themeId || 0 },
    { skip: !app?.id || !themeId }
  )

  useEffect(() => {
    // 0. Only sync after the API query has successfully completed
    //    This prevents setting hasChanges=true before the server response arrives
    if (isUninitialized || isLoading || !isSuccess) {
      return
    }

    // 1. if user hasChanges -> should not override user changes with back-end data
    if (hasChanges) {
      return
    }

    // 2. Determine which data is more recent: query or mutation
    let navigationTabs = apiResponse?.navigation_tabs || []

    // Use mutation data if it's more recent than query data, or if query data is empty
    if (mutationResult) {
      const shouldUseMutationData =
        // Use mutation data if query data is empty
        !navigationTabs ||
        navigationTabs.length === 0 ||
        // Or if mutation data is more recent than query data
        (queryFulfilledTimeStamp &&
          mutationResult.fulfilledTimeStamp &&
          mutationResult.fulfilledTimeStamp > queryFulfilledTimeStamp)

      if (shouldUseMutationData) {
        navigationTabs = mutationResult.data
      }
    }

    const defaultTabs = getDefaultNavigationTabs(orderingEnabled)

    // 3. Load data from either `navigation_tabs` or `default_tabs`
    //    whichever is available, in this order
    if (navigationTabs.length > 0) {
      const sliceTabs = convertNavigationTabsApiToSliceFormat(navigationTabs)
      dispatch(reset(sliceTabs))
    } else if (defaultTabs.length > 0) {
      const sliceTabs = convertDefaultTabsApiToSliceFormat(defaultTabs)
      dispatch(reset(sliceTabs))
      dispatch(setChanges())
    }
  }, [
    apiResponse,
    mutationResult,
    queryFulfilledTimeStamp,
    hasChanges,
    dispatch,
    isSuccess,
    isUninitialized,
    isLoading,
    orderingEnabled,
  ])
}

// Hook to submit BottomNav changes - follows the same pattern as Style/hooks.ts
export const useSubmitBottomNav = () => {
  const dispatch = useDispatch()
  const app = useSelector(selectApp)
  const themeId = useSelector(selectThemeId)
  const merchant = useSelector(selectCurrentMerchant)
  const navigationTabs = useSelector(selectNavigationTabs)

  const [updateNavigationTabs] = useUpdateNavigationTabsMutation()

  const submit = async () => {
    if (!app?.id || !themeId || !merchant?.id) return Promise.resolve()

    // Convert slice state to API format
    let apiPayload = convertNavigationTabsToApiFormat(navigationTabs)

    try {
      // Set submitting state to true before starting uploads
      dispatch(setSubmitting(true))

      // Pre-upload any default icons that need uploading
      if (hasDefaultIcons(apiPayload)) {
        const uploadResult = await preUploadDefaultIcons(
          {
            appId: app.id,
            themeId,
            navigation_tabs: apiPayload,
          },
          merchant.id,
          dispatch
        )
        apiPayload = uploadResult.navigation_tabs
      }

      const result = await updateNavigationTabs({
        appId: app.id,
        themeId,
        navigation_tabs: apiPayload,
      }).unwrap()

      // Update the BottomNav slice with the server response (includes updated icons)
      const sliceTabs = convertNavigationTabsApiToSliceFormat(result)
      dispatch(reset(sliceTabs))
      // Always reset submitting state
      dispatch(setSubmitting(false))

      return result
    } catch (error) {
      // Always reset submitting state
      dispatch(setSubmitting(false))

      // Re-throw the error so the caller can handle it
      throw error
    }
  }

  return submit
}

// Initialize draft state with data from main BottomNav slice
// This should be called when entering edit mode to ensure draft state is populated
export const useInitDraft = () => {
  const dispatch = useDispatch()
  const navigationTabs = useSelector(selectNavigationTabs)

  useEffect(() => {
    // Copy the current main state to draft state
    if (navigationTabs && navigationTabs.length > 0) {
      dispatch(resetDraft(navigationTabs))
    }
  }, [dispatch, navigationTabs])
}
